import type { Chapter } from '@/components/form/types';
import type { JsonFilter } from '@/hooks/use-table-filter/types';
import type {
  Address,
  Apartment,
  ApartmentContract,
  ApartmentContractRevenueRow,
  ApartmentRequirementProfile,
  ApartmentRevenueRow,
  ApartmentTerms,
  ApartmentVacancyNotation,
  BlockContract,
  BlockContractRevenueRow,
  BrfApartment,
  BrfApartmentInvoiceConfigurationRevenueRow,
  BrfApartmentRevenueRow,
  BrfCompany,
  BrfCompanyAnnualReport,
  Building,
  CommonArea,
  Community,
  Company,
  CustomerEarningsReport,
  CustomerEarningsRevenueSetting,
  ErrandActionDefault,
  ExternalClient,
  GeneralErrandDefaultDescription,
  IndustrialPremises,
  IndustrialPremisesContract,
  IndustrialPremisesContractRevenueRow,
  IndustrialPremisesRevenueRow,
  IndustrialPremisesVacancyNotation,
  InspectionErrandProtocolSetting,
  Organization,
  OrganizationUser,
  OtherContract,
  OtherContractRevenueRow,
  OutdoorArea,
  OutdoorSection,
  OutdoorSectionContract,
  OutdoorSectionContractRevenueRow,
  OutdoorSectionRevenueRow,
  OutdoorSectionVacancyNotation,
  ParkingLot,
  ParkingSpot,
  ParkingSpotContract,
  ParkingSpotContractRevenueRow,
  ParkingSpotRevenueRow,
  ParkingSpotVacancyNotation,
  PriceGroup,
  RealEstate,
  Region,
  Segment,
  Sensor,
  SensorSpecification,
  SwedishPriceBaseValue,
  SwedishReferenceRate,
  Tenant,
} from '@pigello/pigello-matrix';

export type ChapterData = {
  organizationuser: Chapter<OrganizationUser>;
  company: Chapter<Company>;
  region: Chapter<Region>;
  community: Chapter<Community>;
  segment: Chapter<Segment>;
  apartment: Chapter<Apartment>;
  tenant_switch: Chapter<Tenant>;
  brf_member_switch: Chapter<Tenant>;
  tenant_company: Chapter<Tenant>;
  tenant_private: Chapter<Tenant>;
  member_company: Chapter<Tenant>;
  member_private: Chapter<Tenant>;
  building: Chapter<Building>;
  commonarea: Chapter<CommonArea>;
  industrialpremises: Chapter<IndustrialPremises>;
  outdoorarea: Chapter<OutdoorArea>;
  outdoorsection: Chapter<OutdoorSection>;
  parkinglot: Chapter<ParkingLot>;
  parkingspot: Chapter<ParkingSpot>;
  realestate: Chapter<RealEstate>;
  apartmentcontract: Chapter<ApartmentContract>;
  othercontract: Chapter<OtherContract>;
  blockcontract: Chapter<BlockContract>;
  industrialpremisescontract: Chapter<IndustrialPremisesContract>;
  outdoorsectioncontract: Chapter<OutdoorSectionContract>;
  organization: Chapter<Organization>;
  parkingspotcontract: Chapter<ParkingSpotContract>;
  apartmentTerms: Chapter<ApartmentTerms>;
  apartmentrevenuerow: Chapter<ApartmentRevenueRow>;
  apartmentcontractrevenuerow: Chapter<ApartmentContractRevenueRow>;
  industrialpremisesrevenuerow: Chapter<IndustrialPremisesRevenueRow>;
  industrialpremisescontractrevenuerow: Chapter<IndustrialPremisesContractRevenueRow>;
  outdoorsectionrevenuerow: Chapter<OutdoorSectionRevenueRow>;
  outdoorsectioncontractrevenuerow: Chapter<OutdoorSectionContractRevenueRow>;
  parkingspotrevenuerow: Chapter<ParkingSpotRevenueRow>;
  parkingspotcontractrevenuerow: Chapter<ParkingSpotContractRevenueRow>;
  othercontractrevenuerow: Chapter<OtherContractRevenueRow>;
  blockcontractrevenuerow: Chapter<BlockContractRevenueRow>;
  brfapartmentrevenuerow: Chapter<BrfApartmentRevenueRow>;
  brfapartmentinvoiceconfigurationrevenuerow: Chapter<BrfApartmentInvoiceConfigurationRevenueRow>;
  sensor: Chapter<Sensor>;
  pricegroup: Chapter<PriceGroup>;
  sensorspecification: Chapter<SensorSpecification>;
  address: Chapter<Address>;
  externalclient: Chapter<ExternalClient>;
  brfapartment: Chapter<BrfApartment>;
  company_brfVersion: Chapter<Company>;
  brfcompany_financial_plan: Chapter<BrfCompany>;
  brfcompany_statutes_plan: Chapter<BrfCompany>;
  brfcompany_pledges: Chapter<BrfCompany>;
  brfcompany_transaction_fees: Chapter<BrfCompany>;
  brfcompany_reparation_fund: Chapter<BrfCompany>;
  brfcompanyannualreport: Chapter<BrfCompanyAnnualReport>;
  swedishpricebasevalue: Chapter<SwedishPriceBaseValue>;
  apartmentvacancynotation: Chapter<ApartmentVacancyNotation>;
  industrialpremisesvacancynotation: Chapter<IndustrialPremisesVacancyNotation>;
  outdoorsectionvacancynotation: Chapter<OutdoorSectionVacancyNotation>;
  parkingspotvacancynotation: Chapter<ParkingSpotVacancyNotation>;
  swedishreferencerate: Chapter<SwedishReferenceRate>;
  customerearningsrevenuesetting: Chapter<CustomerEarningsRevenueSetting>;
  customerearningsreport: Chapter<CustomerEarningsReport>;
  errandactiondefault: Chapter<ErrandActionDefault>;
  generalerranddefaultdescription: Chapter<GeneralErrandDefaultDescription>;
  inspectionerrandprotocolsetting: Chapter<InspectionErrandProtocolSetting>;
  apartmentrequirementprofile: Chapter<ApartmentRequirementProfile>;
};

const revenueRowFields = <
  T extends
    | ApartmentRevenueRow
    | ApartmentContractRevenueRow
    | IndustrialPremisesRevenueRow
    | IndustrialPremisesContractRevenueRow
    | OutdoorSectionRevenueRow
    | OutdoorSectionContractRevenueRow
    | ParkingSpotRevenueRow
    | ParkingSpotContractRevenueRow
    | BrfApartmentRevenueRow
    | OtherContractRevenueRow
    | BrfApartmentInvoiceConfigurationRevenueRow,
>(): Chapter<T> => [
  {
    title: 'Debiteringsrad',
    fields: ['title', 'article', 'customDimensionValue'],
    filter: {
      article: {
        invoice_types: { __in: '0,2' },
      },
    } as Partial<Record<keyof T, JsonFilter>>,
  },
  {
    title: 'Värde',
    fields: ['unitAmount', 'unitPrice'],
  },
  {
    title: 'Period',
    fields: ['startDate', 'endDate', 'excemptFromMonths'],
  },
  {
    title: 'Övrigt',
    fields: ['periodsAdjustsTotalAmount', 'customId'],
  },
];

const vacancyNotationFields = <
  T extends
    | ApartmentVacancyNotation
    | IndustrialPremisesVacancyNotation
    | OutdoorSectionVacancyNotation
    | ParkingSpotVacancyNotation,
>(): Chapter<T> => [
  {
    title: 'Avtal',
    fields: ['customId'],
  },
  {
    title: 'Period',
    fields: ['startDate', 'endDate'],
  },
];

export const Chapters: ChapterData = {
  apartmentvacancynotation: vacancyNotationFields(),
  industrialpremisesvacancynotation: vacancyNotationFields(),
  outdoorsectionvacancynotation: vacancyNotationFields(),
  parkingspotvacancynotation: vacancyNotationFields(),
  apartmentrevenuerow: revenueRowFields(),
  apartmentcontractrevenuerow: revenueRowFields(),
  industrialpremisesrevenuerow: revenueRowFields(),
  industrialpremisescontractrevenuerow: revenueRowFields(),
  parkingspotrevenuerow: revenueRowFields(),
  parkingspotcontractrevenuerow: revenueRowFields(),
  outdoorsectionrevenuerow: revenueRowFields(),
  outdoorsectioncontractrevenuerow: revenueRowFields(),
  othercontractrevenuerow: revenueRowFields(),
  blockcontractrevenuerow: revenueRowFields(),
  brfapartmentrevenuerow: revenueRowFields(),
  brfapartmentinvoiceconfigurationrevenuerow: revenueRowFields(),
  errandactiondefault: [
    { title: 'Standardåtgärd', fields: ['title', 'code', 'description'] },
  ],
  generalerranddefaultdescription: [
    { title: 'Standardbeskrivning', fields: ['title', 'code', 'description'] },
  ],
  swedishreferencerate: [
    {
      title: 'Referensränta',
      fields: ['asOfDate', 'value'],
    },
  ],
  swedishpricebasevalue: [
    {
      title: 'Prisbasbelopp',
      fields: ['forYear', 'value'],
    },
  ],
  brfcompanyannualreport: [
    {
      title: 'Årsredovisning',
      fields: ['forYear', 'fileData'],
    },
  ],
  brfcompany_financial_plan: [
    {
      title: 'Datum',
      fields: ['financialPlanCreationDate', 'financialPlanRegistrationDate'],
    },
    {
      title: 'Dokument',
      fields: ['financialPlanDocument'],
    },
  ],
  brfcompany_statutes_plan: [
    {
      title: 'Datum',
      fields: ['statutesRegistrationDate'],
    },
    {
      title: 'Dokument',
      fields: ['statutesDocument'],
    },
  ],
  brfcompany_pledges: [
    {
      title: 'Artikel',
      fields: ['pledgeFeeArticle'],
    },
    {
      title: 'Pantsättningsavgift',
      description:
        'Välj mellan en fast avgift eller en procentsats av Svenskt Prisbasbelopp',
      fields: ['pledgeFeeFirmValue', 'pledgeFeeQuota'],
    },
  ],
  brfcompany_transaction_fees: [
    {
      title: 'Inställning',
      fields: ['transactionFeeArticle', 'transactionFeeDebitsSeller'],
    },
    {
      title: 'Avgift',
      description:
        'Välj mellan en fast avgift eller en procentsats av Svenskt Prisbasbelopp',
      fields: ['transactionFeeFirmValue', 'transactionFeeQuota'],
    },
  ],
  brfcompany_reparation_fund: [
    {
      title: 'Inställningar',
      fields: ['reparationFundQuota'],
    },
  ],
  externalclient: [
    {
      title: 'Kundinformation',
      fields: ['name', 'customId', 'orgNo', 'visitingAddress'],
    },
    {
      title: 'Faktureringsinformation',
      fields: ['invoiceEmail', 'invoiceAddress', 'fmi'],
      fieldProps: {
        invoiceAddress: { showRedirectedAddressesByDefault: true },
      },
    },
  ],
  address: [
    {
      title: 'Adress',
      fields: [
        'street',
        'streetNumber',
        'postalCode',
        'city',
        'country',
        'careOf',
        'attention',
        'latitude',
        'longitude',
      ],
    },
    {
      title: 'Övrigt',
      fields: ['customId'],
    },
  ],
  apartmentTerms: [
    {
      title: 'Villkor',
      fields: ['name'],
    },
    {
      title: 'Bekvämligheter',
      fields: [
        'electricitySupply',
        'warmWaterSupply',
        'coldWaterSupply',
        'heatSupply',
        'coldSupply',
      ],
    },
    {
      title: 'Övrigt',
      fields: ['additionalNotations', 'customId'],
    },
  ],
  organization: [{ title: 'Organisation', fields: ['name', 'organization'] }],
  organizationuser: [
    {
      title: 'Användaruppgifter',
      description: 'Grundläggande användaruppgifter',
      fields: [
        'detailAccess',
        'role',
        'firstName',
        'lastName',
        'email',
        'phoneNumber',
        'ssn',
        'birthDate',
      ],
    },
    {
      title: 'Företagsuppifter',
      description: 'Eventuell information om användarens företag',
      fields: ['corporateName', 'orgNo'],
    },
    {
      title: 'Uppdragsgivare',
      fields: ['externalClients'],
    },
    {
      title: 'Övrigt',
      fields: ['activeTo', 'customId', 'communicationName'],
    },
  ],
  segment: [
    {
      title: 'Segment',
      fields: ['name', 'customId', 'realestates'],
    },
    {
      title: 'Kategorier',
      fields: [
        'apartmentCategories',
        'industrialPremisesCategories',
        'parkingSpotCategories',
        'outdoorSectionCategories',
        'brfApartmentCategories',
        'commonAreaCategories',
      ],
    },
  ],
  community: [
    {
      title: 'Område',
      fields: [
        'name',
        'customId',
        'mainAddress',
        'primaryPurposeType',
        'region',
      ],
    },
    {
      title: 'Beskrivningar',
      fields: ['salesDescription'],
    },
  ],
  region: [
    {
      title: 'Region',
      fields: ['name', 'customId', 'mainAddress'],
    },
    {
      title: 'Beskrivningar',
      fields: ['salesDescription'],
    },
  ],
  company: [
    {
      title: 'Bolag',
      fields: [
        'name',
        'customId',
        'orgNo',
        'fui',
        'sniNumber',
        'postalAddress',
        'visitingAddress',
        'registeredAddress',
      ],
      fieldProps: {
        postalAddress: { showRedirectedAddressesByDefault: true },
      },
    },
    {
      title: 'Bolagsdetaljer',
      fields: ['taxRegistered', 'vatApproved', 'vatCode'],
    },
    {
      title: 'Uppdragsgivare',
      fields: ['managedBy'],
    },
    {
      title: 'Logotyp',
      fields: ['logo'],
    },
  ],
  company_brfVersion: [
    {
      title: 'Förening',
      fields: [
        'name',
        'customId',
        'orgNo',
        'fui',
        'sniNumber',
        'postalAddress',
        'visitingAddress',
        'registeredAddress',
      ],
      fieldProps: {
        postalAddress: { showRedirectedAddressesByDefault: true },
      },
    },
    {
      title: 'Detaljer',
      fields: ['taxRegistered', 'vatApproved', 'vatCode'],
    },
    {
      title: 'Uppdragsgivare',
      fields: ['managedBy'],
    },
    {
      title: 'Logotyp',
      fields: ['logo'],
    },
  ],

  apartment: [
    {
      title: 'Lägenhet',
      fields: [
        'landlordCustomId',
        'apartmentId',
        'address',
        'building',
        'category',
        'floors',
      ],
    },
    {
      title: 'Yta',
      fields: ['usableArea', 'biArea', 'temperedArea'],
    },
    // {
    //   title: 'Uthyrning',
    //   fields: ['allowedTargetAudiences', 'terms'],
    // },
    {
      title: 'Övrigt',
      fields: ['financialHandler', 'customId', 'popularName'],
    },
  ],
  brfapartment: [
    {
      title: 'Bostadsrätt',
      fields: [
        'landlordCustomId',
        'apartmentId',
        'address',
        'building',
        'category',
        'floors',
      ],
    },
    {
      title: 'Yta',
      fields: ['usableArea', 'biArea', 'temperedArea'],
    },
    {
      title: 'Ekonomi',
      fields: ['quota', 'placementFee', 'placementDate', 'investmentCapital'],
    },
    {
      title: 'Inre fond',
      fields: ['reparationFundQuota'],
    },

    {
      title: 'Övrigt',
      fields: ['financialHandler', 'customId', 'popularName'],
    },
  ],
  tenant_switch: [
    {
      title: 'Privathyresgästinformation',
      description: 'Ange kontaktinformationsuppgifter till hyresgästen',
      fields: ['firstName', 'lastName'],
    },
    {
      title: 'Bolagshyresgästinformation',
      description: 'Ange information om bolagshyresgäst',
      fields: ['corporateName', 'orgNo', 'sniNumber'],
    },
  ],
  brf_member_switch: [
    {
      title: 'Privatmedlemsinformation',
      description: 'Ange kontaktinformationsuppgifter till medlemmen',
      fields: ['firstName', 'lastName'],
    },
    {
      title: 'Bolagsmedlemsinformation',
      description: 'Ange information om bolagsmedlem',
      fields: ['corporateName', 'orgNo', 'sniNumber'],
    },
  ],
  tenant_company: [
    {
      title: 'Bolagsuppgifter',
      description: 'Ange information om bolagshyresgäst',
      fields: ['corporateName', 'orgNo', 'sniNumber'],
    },
    {
      title: 'Kontaktuppgifter',
      description: 'Ange kontaktinformationsuppgifter till hyresgästen',
      fields: ['firstName', 'lastName', 'email', 'phoneNumber', 'isActive'],
    },
    {
      title: 'Avisering',
      description: 'Ange information om avisering',
      fields: [
        'invoiceEmail',
        'invoiceAddress',
        'peppolId',
        'fmi',
        'preferInvoiceViewOnly',
        'invoiceDistributionInstruction',
        'reminderDistributionInstruction',
        'debtDistributionInstruction',
        'customDimensionValue',
        'customDimensionCategoryValue',
        'autogiroDisablesInvoiceDistribution',
      ],
      fieldProps: {
        invoiceDistributionInstruction: { allowInherit: true },
        reminderDistributionInstruction: { allowInherit: true },
        debtDistributionInstruction: { allowInherit: true },
      },
    },
    {
      title: 'Övrigt',
      description: 'Övrig information om hyresgäst',
      fields: [
        'customId',
        'futureAddress',
        'previousAddress',
        'realestates',
        'highRiskInteraction',
        'wantsEvicted',
      ],
    },
  ],
  tenant_private: [
    {
      title: 'Hyresgästinformation',
      description: 'Ange kontaktinformationsuppgifter till hyresgästen',
      fields: [
        'firstName',
        'lastName',
        'ssn',
        'birthDate',
        'email',
        'phoneNumber',
        'currentRegisteredAddress',
        'isActive',
      ],
    },
    {
      title: 'Avisering',
      description: 'Ange information om avisering',
      fields: [
        'invoiceEmail',
        'invoiceAddress',
        'fmi',
        'preferInvoiceViewOnly',
        'invoiceDistributionInstruction',
        'reminderDistributionInstruction',
        'debtDistributionInstruction',
        'customDimensionValue',
        'customDimensionCategoryValue',
        'autogiroDisablesInvoiceDistribution',
      ],
      fieldProps: {
        invoiceDistributionInstruction: { allowInherit: true },
        reminderDistributionInstruction: { allowInherit: true },
        debtDistributionInstruction: { allowInherit: true },
      },
    },
    {
      title: 'Övrigt',
      description: 'Övrig information om hyresgäst',
      fields: [
        'customId',
        'futureAddress',
        'previousAddress',
        'realestates',
        'highRiskInteraction',
        'wantsEvicted',
      ],
    },
  ],
  member_company: [
    {
      title: 'Bolagsuppgifter',
      description: 'Ange information om bolagsmedlem',
      fields: ['corporateName', 'orgNo', 'sniNumber'],
    },
    {
      title: 'Kontaktuppgifter',
      description: 'Ange kontaktinformationsuppgifter till medlemmen',
      fields: ['firstName', 'lastName', 'email', 'phoneNumber', 'isActive'],
    },
    {
      title: 'Avisering',
      description: 'Ange information om avisering',
      fields: [
        'invoiceEmail',
        'invoiceAddress',
        'peppolId',
        'fmi',
        'preferInvoiceViewOnly',
        'invoiceDistributionInstruction',
        'reminderDistributionInstruction',
        'debtDistributionInstruction',
        'customDimensionValue',
        'customDimensionCategoryValue',
        'autogiroDisablesInvoiceDistribution',
      ],
      fieldProps: {
        invoiceDistributionInstruction: { allowInherit: true },
        reminderDistributionInstruction: { allowInherit: true },
        debtDistributionInstruction: { allowInherit: true },
      },
    },
    {
      title: 'Övrigt',
      description: 'Övrig information om medlem',
      fields: [
        'customId',
        'futureAddress',
        'previousAddress',
        'realestates',
        'highRiskInteraction',
        'wantsEvicted',
      ],
    },
  ],
  member_private: [
    {
      title: 'Medlemsinformation',
      description: 'Ange kontaktinformationsuppgifter till medlemmen',
      fields: [
        'firstName',
        'lastName',
        'ssn',
        'birthDate',
        'email',
        'phoneNumber',
        'currentRegisteredAddress',
        'isActive',
      ],
    },
    {
      title: 'Avisering',
      description: 'Ange information om avisering',
      fields: [
        'invoiceEmail',
        'invoiceAddress',
        'fmi',
        'preferInvoiceViewOnly',
        'invoiceDistributionInstruction',
        'reminderDistributionInstruction',
        'debtDistributionInstruction',
        'customDimensionValue',
        'customDimensionCategoryValue',
        'autogiroDisablesInvoiceDistribution',
      ],
      fieldProps: {
        invoiceDistributionInstruction: { allowInherit: true },
        reminderDistributionInstruction: { allowInherit: true },
        debtDistributionInstruction: { allowInherit: true },
      },
    },
    {
      title: 'Övrigt',
      description: 'Övrig information om medlem',
      fields: [
        'customId',
        'futureAddress',
        'previousAddress',
        'realestates',
        'highRiskInteraction',
        'wantsEvicted',
      ],
    },
  ],
  building: [
    {
      title: 'Byggnad',
      fields: [
        'name',
        'customId',
        'address',
        'realestate',
        'structureType',
        'structureOperationType',
      ],
    },
    {
      title: 'Yta',
      fields: [
        'registeredGrossArea',
        'registeredBuildingArea',
        'registeredUsableArea',
        'registeredBiArea',
        'registeredTemperedArea',
      ],
    },
    {
      title: 'Detaljer',
      fields: [
        'availabilityStartDate',
        'availabilityEndDate',
        'builtDate',
        'demolitionDate',
      ],
    },
    {
      title: 'Beskrivningar',
      fields: ['salesDescription'],
    },
  ],
  commonarea: [
    {
      title: 'Gemensam yta',
      fields: [
        'landlordCustomId',
        'popularName',
        'address',
        'building',
        'outdoorArea',
        'category',
        'floors',
      ],
    },
    {
      title: 'Yta',
      fields: ['usableArea', 'biArea', 'temperedArea'],
    },
    {
      title: 'Övrigt',
      fields: ['customId'],
    },
  ],
  industrialpremises: [
    {
      title: 'Lokal',
      fields: [
        'landlordCustomId',
        'popularName',
        'address',
        'building',
        'category',
        'floors',
        'isFurnished',
      ],
    },
    {
      title: 'Yta',
      fields: ['usableArea', 'biArea', 'temperedArea'],
    },
    // {
    //   title: 'Uthyrning',
    //   fields: ['allowedTargetAudiences', 'terms'],
    // },
    {
      title: 'Övrigt',
      fields: ['financialHandler', 'customId'],
    },
  ],
  outdoorarea: [
    {
      title: 'Område',
      fields: [
        'name',
        'customId',
        'address',
        'realestate',
        'structureType',
        'structureOperationType',
      ],
    },
    {
      title: 'Yta',
      fields: [
        'registeredGrossArea',
        'registeredBuildingArea',
        'registeredUsableArea',
        'registeredBiArea',
      ],
    },
    {
      title: 'Detaljer',
      fields: [
        'availabilityStartDate',
        'availabilityEndDate',
        'builtDate',
        'demolitionDate',
      ],
    },
    {
      title: 'Beskrivningar',
      fields: ['salesDescription'],
    },
  ],
  outdoorsection: [
    {
      title: 'Utomhussektion',
      fields: [
        'landlordCustomId',
        'popularName',
        'address',
        'outdoorArea',
        'category',
      ],
    },
    {
      title: 'Yta',
      fields: ['usableArea', 'biArea'],
    },
    // {
    //   title: 'Uthyrning',
    //   fields: ['allowedTargetAudiences', 'terms'],
    // },
    {
      title: 'Övrigt',
      fields: ['financialHandler', 'customId'],
    },
  ],
  parkinglot: [
    {
      title: 'Parkeringsområde',
      fields: [
        'name',
        'customId',
        'address',
        'realestate',
        'structureType',
        'structureOperationType',
        'isGarage',
      ],
    },
    {
      title: 'Specifikationer',
      fields: ['maxHeight', 'maxWeight', 'maxWidth'],
    },
    {
      title: 'Yta',
      fields: [
        'registeredGrossArea',
        'registeredBuildingArea',
        'registeredUsableArea',
        'registeredBiArea',
      ],
    },
    {
      title: 'Detaljer',
      fields: [
        'availabilityStartDate',
        'availabilityEndDate',
        'builtDate',
        'demolitionDate',
      ],
    },
    {
      title: 'Beskrivningar',
      fields: ['salesDescription'],
    },
  ],
  parkingspot: [
    {
      title: 'Fordonsplats',
      fields: [
        'landlordCustomId',
        'popularName',
        'address',
        'parkingLot',
        'building',
        'category',
        'floors',
      ],
    },
    {
      title: 'Specifikationer',
      fields: ['maxHeight', 'maxWeight', 'maxWidth'],
    },
    {
      title: 'Yta',
      fields: ['usableArea', 'biArea'],
    },
    // {
    //   title: 'Uthyrning',
    //   fields: ['allowedTargetAudiences', 'terms'],
    // },
    {
      title: 'Övrigt',
      fields: ['financialHandler', 'customId'],
    },
  ],
  realestate: [
    {
      title: 'Fastighet',
      fields: ['name', 'customId', 'popularName', 'mainAddress', 'community'],
    },
    {
      title: 'Användning',
      fields: ['publicUsage', 'typeCode'],
    },
    {
      title: 'Yta',
      fields: ['registeredLandArea', 'registeredWaterArea'],
    },
    {
      title: 'Detaljer',
      fields: [
        'landlord',
        'primaryFinancialHandler',
        'ownedBy',
        'ownershipType',
        'purchasedDate',
        'soldDate',
        'financialResponsibilityFrom',
        'financialResponsibilityTo',
        'nonFinancialResponsibilityFrom',
        'nonFinancialResponsibilityTo',
      ],
    },
  ],
  apartmentcontract: [
    {
      title: 'Avtal',
      fields: ['idNumber'],
    },
    {
      title: 'Objekt',
      fields: ['space'],
    },
    {
      title: 'Parter',
      fields: ['tenant', 'biTenants'],
    },
    {
      title: 'Period',
      fields: [
        'startDate',
        'initialEndDate',
        'renewIndefinetely',
        'renewIntervalWeeks',
        'ownerNotificationIntervalWeeks',
        'counterpartNotificationIntervalWeeks',
        'ownerWarningIntervalWeeks',
        'counterpartWarningIntervalWeeks',
      ],
    },
    {
      title: 'Hantering av debiteringsrader',
      fields: ['invoiceMergeIndexInView', 'invoiceMergeNonBaseRentInView'],
    },
  ],
  blockcontract: [
    {
      title: 'Avtal',
      fields: ['idNumber'],
    },
    {
      title: 'Objekt',
      fields: [
        'apartments',
        'parkingSpots',
        'industrialPremises',
        'outdoorSections',
      ],
    },
    {
      title: 'Avisering',
      fields: [
        'invoicingCompany',
        'mainRealestate',
        'invoiceMergeIndexInView',
        'invoiceMergeNonBaseRentInView',
      ],
    },
    {
      title: 'Parter',
      fields: ['tenant', 'biTenants'],
    },
    {
      title: 'Period',
      fields: [
        'startDate',
        'initialEndDate',
        'renewIndefinetely',
        'renewIntervalWeeks',
        'ownerNotificationIntervalWeeks',
        'counterpartNotificationIntervalWeeks',
        'ownerWarningIntervalWeeks',
        'counterpartWarningIntervalWeeks',
      ],
    },
  ],
  othercontract: [
    {
      title: 'Avtal',
      fields: ['idNumber'],
    },
    {
      title: 'Aviseringsinställningar',
      fields: [
        'invoicingCompany',
        'invoicingContractSetting',
        'invoicingPaymentTerms',
      ],
    },
    {
      title: 'Parter',
      fields: ['tenantCounterpart'],
    },
    {
      title: 'Period',
      fields: [
        'startDate',
        'initialEndDate',
        'renewIndefinetely',
        'renewIntervalWeeks',
        'ownerNotificationIntervalWeeks',
        'counterpartNotificationIntervalWeeks',
        'ownerWarningIntervalWeeks',
        'counterpartWarningIntervalWeeks',
      ],
    },
  ],
  industrialpremisescontract: [
    {
      title: 'Avtal',
      fields: ['idNumber'],
    },
    {
      title: 'Objekt',
      fields: ['space'],
    },
    {
      title: 'Parter',
      fields: ['tenant', 'biTenants'],
    },
    {
      title: 'Period',
      fields: [
        'startDate',
        'initialEndDate',
        'renewIndefinetely',
        'renewIntervalWeeks',
        'ownerNotificationIntervalWeeks',
        'counterpartNotificationIntervalWeeks',
        'ownerWarningIntervalWeeks',
        'counterpartWarningIntervalWeeks',
      ],
    },
    {
      title: 'Hantering av debiteringsrader',
      fields: ['invoiceMergeIndexInView', 'invoiceMergeNonBaseRentInView'],
    },
  ],
  outdoorsectioncontract: [
    {
      title: 'Avtal',
      fields: ['idNumber'],
    },
    {
      title: 'Objekt',
      fields: ['space'],
    },
    {
      title: 'Parter',
      fields: ['tenant', 'biTenants'],
    },
    {
      title: 'Period',
      fields: [
        'startDate',
        'initialEndDate',
        'renewIndefinetely',
        'renewIntervalWeeks',
        'ownerNotificationIntervalWeeks',
        'counterpartNotificationIntervalWeeks',
        'ownerWarningIntervalWeeks',
        'counterpartWarningIntervalWeeks',
      ],
    },
    {
      title: 'Hantering av debiteringsrader',
      fields: ['invoiceMergeIndexInView', 'invoiceMergeNonBaseRentInView'],
    },
  ],
  parkingspotcontract: [
    {
      title: 'Avtal',
      fields: ['idNumber'],
    },
    {
      title: 'Objekt',
      fields: ['space'],
    },
    {
      title: 'Parter',
      fields: ['tenant', 'biTenants'],
    },
    {
      title: 'Period',
      fields: [
        'startDate',
        'initialEndDate',
        'renewIndefinetely',
        'renewIntervalWeeks',
        'ownerNotificationIntervalWeeks',
        'counterpartNotificationIntervalWeeks',
        'ownerWarningIntervalWeeks',
        'counterpartWarningIntervalWeeks',
      ],
    },
    {
      title: 'Hantering av debiteringsrader',
      fields: ['invoiceMergeIndexInView', 'invoiceMergeNonBaseRentInView'],
    },
  ],
  sensor: [
    {
      title: 'Sensor',
      fields: [
        'externalId',
        'name',
        'specification',
        'customId',
        'billingEnabled',
        'granularity',
        'outputMode',
        'description',
      ],
    },
    {
      title: 'Specifikationer',
      fields: [
        'apartment',
        'commonArea',
        'industrialPremises',
        'parkingSpot',
        'brfApartment',
      ],
    },
  ],
  sensorspecification: [
    {
      title: 'Mätartyp',
      fields: ['name', 'customId', 'unitName', 'billingArticle', 'category'],
      filter: {
        billingArticle: {
          invoice_types: { noop: 2 },
        },
      },
    },
  ],
  pricegroup: [
    {
      title: 'Prisgrupp',
      fields: [
        'name',
        'customId',
        'sensorSpecification',
        'periodsOffset',
        'description',
      ],
    },
    // {
    //   title: 'Intervaller',
    //   fields: [
    //     'autoAcceptDelayDays',
    //     'targetPriceChangeIntervalDays',
    //     'targetPriceChangeDayOfMonth',
    //   ],
    // },
  ],
  customerearningsrevenuesetting: [
    {
      title: 'Omsättning',
      fields: [
        'name',
        'customId',
        'description',
        'article',
        'firmValueIndexationSetting',
        'firmValueIndexationOverrideBaseYear',
      ],
    },
    {
      title: 'Prisinställningar',
      fields: [
        'quota',
        'startReportingFromMonth',
        'reportingMonthOffset',
        'reportingMonthFrequency',
        'minValue',
        'maxValue',
        'missingReportValue',
        'missingReportPenaltyFee',
        'missingReportPenaltyFeeInterval',
        'missingReportPenaltyMonths',
        'preFirstReportValue',
        'reportDueNotifyDays',
        'reportLateNotifyIntervalDays',
        'firmValueIndexationSetting',
        'firmValueIndexationOverrideBaseYear',
      ],
    },
  ],
  customerearningsreport: [
    {
      title: 'Omsättning',
      description: 'Ange omsättning för en given månad för hyregästen',
      fields: ['value', 'grossValue', 'includesSublettedSpace'],
    },
    {
      title: 'Period',
      description: 'Ange perioden för omsättningen',
      fields: ['periodStart', 'periodEnd', 'reportedAt'],
    },
    {
      title: 'Övright',
      fields: [
        'customId',
        'accountantCompanyName',
        'accountantWorkEmail',
        'accountantWorkPhone',
      ],
    },
  ],
  inspectionerrandprotocolsetting: [
    {
      title: 'Protokoll',
      fields: ['name', 'customId'],
    },
    {
      title: 'Inställningar',
      fields: [
        'showSpaceInfo',
        'showComponentConditions',
        'showFaults',
        'showResponsibleParties',
        'showTenantAssociations',
        'showFaultsImages',
        'showNotesImages',
        'showNotes',
        'showFaultsDescriptions',
        'showFaultsActionProposals',
        'showChecklists',
        'showNominalComponents',
        'showPerformers',
      ],
    },
    {
      title: 'Filer',
      fields: ['showLogo', 'includeAttachment'],
    },
  ],
  apartmentrequirementprofile: [
    {
      title: 'Allmänt',
      fields: ['name', 'customId'],
    },
    {
      title: 'Detaljer',
      fields: [
        'isPrivateAllowed',
        'isCorporateAllowed',
        'audience',
        'minAge',
        'maxAge',
      ],
    },
    {
      title: 'Ekonomi',
      fields: [
        'allowedIncomeSources',
        'typeOfIncomeFactor',
        'preRentNetIncomeFactor',
        'postRentNetIncomeFactor',
      ],
    },
  ],
};
